import { useState } from 'react';
import { Navigate, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAccess } from '../../hooks/useAccess';
import { ArrowRight } from 'lucide-react';
import { TodayPanel } from './TodayPanel';
import { PageShell } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';

const quickLinks = [
  { name: 'Sales', feature: 'sales', href: '/sales', description: 'Pipeline & outreach' },
  { name: 'CSM', feature: 'csm', href: '/csm', description: 'Customers & health' },
  { name: 'GTM', feature: 'gtm', href: '/gtm', description: 'Signals & sourcing' },
  { name: 'Marketing', feature: 'marketing', href: '/marketing', description: 'Content & visibility' },
  { name: 'Finance', feature: 'finance', href: '/finance', description: 'MRR & contracts' },
] as const;

/**
 * Home — company-wide overview. Replaces the old multi-tab Dashboard; the CS and
 * Measurement tabs now live under their own departments (CSM, Marketing), so we
 * redirect the legacy `?tab=` links there.
 */
export function Home() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canAccessArea } = useAccess();
  const [draft, setDraft] = useState('');

  const legacyTab = searchParams.get('tab');
  if (legacyTab === 'csm') return <Navigate to="/csm" replace />;
  if (legacyTab === 'measurement') return <Navigate to="/marketing/visibility" replace />;

  const firstName = user?.name?.split(' ')[0] ?? 'there';
  const canAskJames = canAccessArea('james');
  const visibleQuickLinks = quickLinks.filter((link) => canAccessArea(link.feature));

  const handleAsk = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = draft.trim();
    navigate('/james', trimmed ? { state: { draft: trimmed } } : undefined);
  };

  return (
    <PageShell className="max-w-5xl">
      {/* Greeting hero */}
      <div className="flex flex-col items-center pt-8 pb-2 text-center">
        <h1 className="font-display text-display-sm text-text-primary">Hey {firstName}</h1>
        <p className="mt-2 text-body-md text-text-tertiary">This is Sequesto, at a glance</p>

        {canAskJames && (
          <form onSubmit={handleAsk} className="mt-8 w-full max-w-2xl">
            <div className="rounded-2xl border border-border-light bg-surface-primary p-3 shadow-soft transition-shadow focus-within:border-border focus-within:shadow-medium">
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Ask James…"
                className="w-full bg-transparent px-2 py-1.5 text-body-md text-text-primary placeholder-text-muted focus:outline-none"
              />
              <div className="mt-2 flex items-center justify-end">
                <Button type="submit" rightIcon={<ArrowRight className="h-3.5 w-3.5" aria-hidden />}>
                  Ask
                </Button>
              </div>
            </div>
          </form>
        )}

        {visibleQuickLinks.length > 0 && (
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {visibleQuickLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="group flex items-center gap-2 rounded-full border border-border-light bg-surface-primary px-4 py-1.5 text-body-sm text-text-secondary transition-colors hover:border-border hover:text-text-primary"
              >
                <span className="font-medium">{link.name}</span>
                <span className="text-body-xs text-text-muted group-hover:text-text-tertiary">{link.description}</span>
              </Link>
            ))}
          </div>
        )}
      </div>

      <TodayPanel />
    </PageShell>
  );
}
